import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getDateFinEffective, AffectationRetard, SuspensionInfo } from '@/lib/dateUtils';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { queryKeys } from '@/lib/queryKeys';

interface UseChantierDateFinParams {
  chantierId: string;
  dateFinEstimee: string | null;
  dateFinInitiale?: string | null;
  enabled?: boolean;
}

interface DateFinEffectiveData {
  dateEffective: Date | null;
  totalJoursRetard: number;
  totalJoursSuspension: number;
  isLoading: boolean;
}

/**
 * Hook pour obtenir la date de fin effective d'un chantier.
 * Charge automatiquement les retards et suspensions terminées.
 *
 * La date effective = date_fin_initiale + jours retard + jours suspension
 *
 * @param chantierId - ID du chantier
 * @param dateFinEstimee - Date de fin estimée actuelle
 * @param dateFinInitiale - Date de fin initiale (avant retards)
 * @param enabled - Activer/désactiver le hook
 */
export function useChantierDateFin({
  chantierId,
  dateFinEstimee,
  dateFinInitiale,
  enabled = true
}: UseChantierDateFinParams): DateFinEffectiveData & { refetch: () => void } {
  const { entrepriseId } = useSubscription();

  const query = useQuery({
    queryKey: queryKeys.chantiers.dateFin(entrepriseId!, chantierId),
    queryFn: async () => {
      // Charger affectations retard (notes LIKE 'Retard chantier%')
      const { data: affectations } = await supabase
        .from('affectations_chantiers')
        .select('date_fin, jours_travailles, jours_details')
        .eq('chantier_id', chantierId)
        .like('notes', 'Retard chantier%');

      // Charger suspensions terminées (avec date_reprise)
      const { data: suspensions } = await supabase
        .from('suspensions_chantiers')
        .select('date_suspension, date_reprise, duree_suspension_jours')
        .eq('chantier_id', chantierId)
        .not('date_reprise', 'is', null);

      // Convertir les données pour le calcul
      const affectationsRetard: AffectationRetard[] = (affectations || []).map(a => ({
        date_fin: a.date_fin,
        jours_travailles: a.jours_travailles || 0,
        jours_details: a.jours_details as AffectationRetard['jours_details']
      }));

      const suspensionsTerminees: SuspensionInfo[] = (suspensions || []).map(s => ({
        date_suspension: s.date_suspension,
        date_reprise: s.date_reprise,
        duree_suspension_jours: s.duree_suspension_jours
      }));

      // Calculer totaux
      // IMPORTANT: Ne pas sommer jours_travailles car c'est par membre d'équipe
      // Ex: 1 jour de retard avec 3 membres = 3 affectations avec jours_travailles=1
      // On doit compter les jours UNIQUES via jours_details
      let totalJoursRetard = 0;
      if (affectationsRetard.length > 0) {
        const uniqueDelayDays = new Set<string>();
        for (const aff of affectationsRetard) {
          if (aff.jours_details && Array.isArray(aff.jours_details)) {
            for (const detail of aff.jours_details as Array<{ date: string; period: string; value: number }>) {
              uniqueDelayDays.add(`${detail.date}:${detail.period}`);
            }
          }
        }

        if (uniqueDelayDays.size > 0) {
          for (const key of uniqueDelayDays) {
            const period = key.split(':')[1];
            totalJoursRetard += period === 'FULL' ? 1 : 0.5;
          }
        } else {
          // Fallback si pas de jours_details: prendre le max jours_travailles
          totalJoursRetard = Math.max(...affectationsRetard.map(a => a.jours_travailles || 0), 0);
        }
      }

      const totalJoursSuspension = suspensionsTerminees.reduce(
        (sum, s) => sum + (s.duree_suspension_jours || 0),
        0
      );

      // Calculer date effective
      const dateEffective = getDateFinEffective({
        date_fin_estimee: dateFinEstimee,
        date_fin_initiale: dateFinInitiale,
        affectationsRetard,
        suspensionsTerminees
      });

      return {
        dateEffective,
        totalJoursRetard,
        totalJoursSuspension
      };
    },
    enabled: enabled && !!entrepriseId && !!chantierId && !!dateFinEstimee,
    staleTime: 2 * 60 * 1000, // 2 min
  });

  return {
    dateEffective: query.data?.dateEffective ?? null,
    totalJoursRetard: query.data?.totalJoursRetard ?? 0,
    totalJoursSuspension: query.data?.totalJoursSuspension ?? 0,
    isLoading: query.isLoading,
    refetch: query.refetch
  };
}
